import { memo, useId, type RefObject } from "react";

import { cn } from "@/utils/cn";

interface MayaSvgAvatarProps {
  /** Overrides the wrapper's size classes (the FAB and the chat header differ). */
  className?: string;
  /** Face group — tilted toward the cursor by useCursorTracking. */
  headRef?: RefObject<SVGGElement | null>;
  /** Eye groups — translated toward the cursor (the pupils follow it). */
  leftEyeRef?: RefObject<SVGGElement | null>;
  rightEyeRef?: RefObject<SVGGElement | null>;
}

/**
 * Maya's face, inlined from public/icons/maya.svg so the head can tilt toward
 * the cursor, the eyes can follow it, and blink. Each eye nests two groups: the
 * outer one is translated toward the cursor (JS), the inner one blinks (CSS) —
 * separate elements so the two transforms compose instead of overriding each
 * other. Background sparkles are supplied by the orbit particles around the FAB.
 *
 * KEEPING THIS IN SYNC WITH THE ASSET: public/icons/maya.svg is a design
 * reference only — nothing loads it at runtime, so editing it alone changes
 * nothing on screen. When the artwork changes, re-inline it here and re-attach
 * the three refs plus the `maya-face` / `maya-eye maya-animated` class hooks
 * (see maya-copilot.css) — a plain Figma export has none of them. Element order
 * matches the asset exactly so paint order is preserved.
 */
export const MayaSvgAvatar = memo(
  ({ headRef, leftEyeRef, rightEyeRef, className }: MayaSvgAvatarProps) => {
    // This avatar renders in more than one place at once (the FAB and the chat
    // header), and SVG `id`s are document-global — two instances would emit
    // duplicate ids and every `url(#…)` would resolve to whichever mounted
    // first. Scope them per instance. useId() contains colons, which are legal
    // in an id but break CSS selectors, so strip them.
    const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
    const id = (name: string) => `${uid}-${name}`;
    const ref = (name: string) => `url(#${id(name)})`;

    return (
      <div
        className={cn(
          "relative flex items-center justify-center",
          className ?? "h-11 w-11 sm:h-12 sm:w-12",
        )}
      >
        <svg
          viewBox="0 0 694 633"
          width="100%"
          height="100%"
          fill="none"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
          className="pointer-events-none overflow-visible"
        >
          {/* Face — tilts with the cursor */}
          <g ref={headRef ?? null} className="maya-face">
            <path
              d="M390.677 17.2343C138.959 -2.81726 52.5334 194.623 40.7855 295.849C6.16956 594.114 185.655 591.242 324.863 603.558C577.381 603.558 657.365 533.087 657.365 406.641C657.365 121.159 530.51 43.4226 390.677 17.2343Z"
              fill={ref("paint0_linear_352_10627")}
            />
            {/* Soft top-left sheen over the head */}
            <path
              d="M390.677 17.2343C138.959 -2.81726 52.5334 194.623 40.7855 295.849C6.16956 594.114 185.655 591.242 324.863 603.558C577.381 603.558 657.365 533.087 657.365 406.641C657.365 121.159 530.51 43.4226 390.677 17.2343Z"
              fill={ref("paint1_radial_352_10627")}
            />
            <mask
              id={id("mask0_352_10627")}
              style={{ maskType: "alpha" }}
              maskUnits="userSpaceOnUse"
              x="36"
              y="15"
              width="622"
              height="589"
            >
              <path
                d="M390.677 17.2343C138.959 -2.81726 52.5334 194.623 40.7855 295.849C6.16956 594.114 185.655 591.242 324.863 603.558C577.381 603.558 657.365 533.087 657.365 406.641C657.365 121.159 530.51 43.4226 390.677 17.2343Z"
                fill={ref("paint2_linear_352_10627")}
              />
            </mask>
            <g mask={ref("mask0_352_10627")}>
              <path
                d="M175.672 762.393C110.81 822.655 72.2999 760.405 61.1528 721.746L128.299 495.22C55.6199 361.306 132.11 124.538 346.42 98.5572C517.868 77.7722 718.618 252.905 797.561 343.07L791.595 653.182C751.601 752.989 683.44 730.736 646.699 712.354C578.904 682.481 571.589 719.663 573.572 750.217C574.003 845.604 522.147 799.111 496.166 763.942C453.966 704.119 422.874 729.721 414.48 753.53C392.202 816.715 374.811 788.035 367.037 761.942C354.593 730.496 310.479 723.037 304.99 755.774C294.308 819.479 253.716 810.365 248.846 782.899C234.622 729.791 194.137 747.1 175.672 762.393Z"
                fill="white"
                fillOpacity="0.2"
              />
              <path
                d="M177.221 779.804C112.359 840.067 73.8492 777.816 62.7021 739.158L129.848 512.632C57.1692 378.718 133.66 141.95 347.969 115.969C519.417 95.1839 720.167 270.317 799.111 360.482L793.144 670.593C753.15 770.401 684.989 748.147 648.248 729.766C580.453 699.893 573.138 737.075 575.122 767.628C575.552 863.015 523.697 816.523 497.715 781.353C455.515 721.531 424.423 747.133 416.029 770.941C393.751 834.127 376.361 805.447 368.586 779.354C356.142 747.908 312.028 740.449 306.539 773.186C295.857 836.891 255.266 827.777 250.396 800.31C236.171 747.203 195.686 764.512 177.221 779.804Z"
                fill="white"
                fillOpacity="0.2"
              />
              <circle
                cx="12.314"
                cy="12.314"
                r="12.314"
                transform="matrix(-0.736169 -0.676798 -0.676798 0.736169 402.604 615.167)"
                fill="#FFBFD9"
              />
              <circle
                cx="12.314"
                cy="12.314"
                r="12.314"
                transform="matrix(-0.999997 0.00232737 0.00232737 0.999997 590.148 545.258)"
                fill="#FFBFD9"
              />
              <circle
                cx="13.135"
                cy="13.135"
                r="13.135"
                transform="matrix(-0.999997 0.00232737 0.00232737 0.999997 560.588 542.042)"
                fill="#FFBFD9"
              />
            </g>
            <path
              d="M390.677 17.2343C138.959 -2.81726 52.5334 194.623 40.7855 295.849C6.16956 594.114 185.655 591.242 324.863 603.558C577.381 603.558 657.365 533.087 657.365 406.641C657.365 121.159 530.51 43.4226 390.677 17.2343Z"
              stroke="#4C1D95"
              strokeWidth="15"
              strokeLinejoin="round"
            />
            <g filter={ref("filter0_d_352_10627")}>
              <rect
                x="112.281"
                y="208.795"
                width="469.923"
                height="201.783"
                rx="62.2953"
                fill={ref("paint3_linear_352_10627")}
              />
              <rect
                x="101.024"
                y="197.538"
                width="492.438"
                height="224.297"
                rx="73.5525"
                stroke={ref("paint4_linear_352_10627")}
                strokeWidth="22.5143"
              />
            </g>
            <g filter={ref("filter1_i_352_10627")}>
              <rect
                x="112.281"
                y="208.795"
                width="469.923"
                height="201.783"
                rx="62.2953"
                fill="#141414"
              />
            </g>
            <rect
              x="118.177"
              y="214.692"
              width="458.13"
              height="189.989"
              rx="56.3987"
              stroke="#9866E6"
              strokeWidth="11.7932"
            />
            <g filter={ref("filter2_i_352_10627")}>
              <rect
                x="106.384"
                y="202.899"
                width="481.717"
                height="213.576"
                rx="68.1919"
                stroke="#4C1D95"
                strokeWidth="11.7932"
              />
            </g>
            <g ref={leftEyeRef ?? null}>
              <g
                className="maya-eye maya-animated"
                filter={ref("filter3_d_352_10627")}
              >
                <rect
                  x="200"
                  y="270.176"
                  width="78"
                  height="79"
                  rx="11.5111"
                  fill="white"
                />
                <rect
                  x="200"
                  y="270.176"
                  width="78"
                  height="79"
                  rx="11.5111"
                  fill="#1AD598"
                />
              </g>
            </g>
            <g ref={rightEyeRef ?? null}>
              <g
                className="maya-eye maya-animated"
                filter={ref("filter4_d_352_10627")}
              >
                <rect
                  x="423"
                  y="270.176"
                  width="79"
                  height="79"
                  rx="11.5111"
                  fill="white"
                />
                <rect
                  x="423"
                  y="270.176"
                  width="79"
                  height="79"
                  rx="11.5111"
                  fill="#1AD598"
                />
              </g>
            </g>
            {/* Left ear + its shaded underside */}
            <circle cx="83.9633" cy="537.139" r="83.9633" fill="#DB317B" />
            <path
              d="M16.376 487.315C12.2685 497.217 10.0001 508.075 10 519.463C10 565.834 47.5914 603.427 93.9629 603.427C121.679 603.427 146.258 589.996 161.549 569.289C148.93 599.707 118.946 621.103 83.9629 621.103C37.5914 621.102 0 583.51 0 537.139C8.2935e-05 518.485 6.08493 501.252 16.376 487.315Z"
              fill="black"
              fillOpacity="0.26"
            />
            <circle cx="608.963" cy="537.463" r="83.9633" fill="#DB317B" />
            <path
              d="M212.495 112.644C226.706 112.644 238.226 100.972 238.226 86.5746C238.226 72.177 226.706 60.5054 212.495 60.5054C198.284 60.5054 186.764 72.177 186.764 86.5746C186.764 100.972 198.284 112.644 212.495 112.644Z"
              fill="white"
            />
            <path
              d="M154.939 152.594C162.419 152.594 168.482 146.379 168.482 138.713C168.482 131.047 162.419 124.832 154.939 124.832C147.46 124.832 141.397 131.047 141.397 138.713C141.397 146.379 147.46 152.594 154.939 152.594Z"
              fill="white"
            />
            <path
              d="M520.925 172.231C526.347 172.231 530.743 167.835 530.743 162.413C530.743 156.99 526.347 152.594 520.925 152.594C515.502 152.594 511.106 156.99 511.106 162.413C511.106 167.835 515.502 172.231 520.925 172.231Z"
              fill="white"
            />
            <path
              d="M500.95 143.115C508.429 143.115 514.492 137.052 514.492 129.572C514.492 122.093 508.429 116.03 500.95 116.03C493.47 116.03 487.407 122.093 487.407 129.572C487.407 137.052 493.47 143.115 500.95 143.115Z"
              fill="white"
            />
            {/* Right ear's shaded underside — painted last, exactly as in the asset */}
            <path
              d="M660.754 471.5C680.577 486.858 693.34 510.894 693.34 537.911C693.34 584.283 655.748 621.875 609.376 621.875C581.177 621.875 556.226 607.972 541 586.648C555.2 597.65 573.023 604.199 592.376 604.199C638.748 604.199 676.34 566.607 676.34 520.235C676.34 502.064 670.565 485.241 660.754 471.5Z"
              fill="black"
              fillOpacity="0.26"
            />
          </g>

          <defs>
            <filter
              id={id("filter0_d_352_10627")}
              x="48.7047"
              y="145.219"
              width="597.076"
              height="328.935"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feMorphology
                radius="10.7211"
                operator="dilate"
                in="SourceAlpha"
                result="effect1_dropShadow_352_10627"
              />
              <feOffset />
              <feGaussianBlur stdDeviation="15.1704" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 1 0 0 0 0 0.556863 0 0 0 0 0.780392 0 0 0 1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_352_10627"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_352_10627"
                result="shape"
              />
            </filter>
            <filter
              id={id("filter1_i_352_10627")}
              x="112.281"
              y="208.795"
              width="469.923"
              height="201.783"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="BackgroundImageFix"
                result="shape"
              />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feMorphology
                radius="17"
                operator="erode"
                in="SourceAlpha"
                result="effect1_innerShadow_352_10627"
              />
              <feOffset />
              <feGaussianBlur stdDeviation="8.09444" />
              <feComposite
                in2="hardAlpha"
                operator="arithmetic"
                k2="-1"
                k3="1"
              />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.596078 0 0 0 0 0.4 0 0 0 0 0.901961 0 0 0 1 0"
              />
              <feBlend
                mode="normal"
                in2="shape"
                result="effect1_innerShadow_352_10627"
              />
            </filter>
            <filter
              id={id("filter2_i_352_10627")}
              x="100.487"
              y="197.002"
              width="493.51"
              height="229.658"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="BackgroundImageFix"
                result="shape"
              />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="4.28845" />
              <feGaussianBlur stdDeviation="4.18124" />
              <feComposite
                in2="hardAlpha"
                operator="arithmetic"
                k2="-1"
                k3="1"
              />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
              />
              <feBlend
                mode="normal"
                in2="shape"
                result="effect1_innerShadow_352_10627"
              />
            </filter>
            <filter
              id={id("filter3_d_352_10627")}
              x="148.7"
              y="218.876"
              width="180.6"
              height="181.6"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feMorphology
                radius="4"
                operator="dilate"
                in="SourceAlpha"
                result="effect1_dropShadow_352_10627"
              />
              <feOffset />
              <feGaussianBlur stdDeviation="23.65" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.101961 0 0 0 0 0.835294 0 0 0 0 0.596078 0 0 0 1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_352_10627"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_352_10627"
                result="shape"
              />
            </filter>
            <filter
              id={id("filter4_d_352_10627")}
              x="371.7"
              y="218.876"
              width="181.6"
              height="181.6"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feMorphology
                radius="4"
                operator="dilate"
                in="SourceAlpha"
                result="effect1_dropShadow_352_10627"
              />
              <feOffset />
              <feGaussianBlur stdDeviation="23.65" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.101961 0 0 0 0 0.835294 0 0 0 0 0.596078 0 0 0 1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_352_10627"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_352_10627"
                result="shape"
              />
            </filter>
            <linearGradient
              id={id("paint0_linear_352_10627")}
              x1="343.771"
              y1="15.8154"
              x2="343.771"
              y2="642.131"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#9E75EB" />
              <stop offset="0.55" stopColor="#7A45C7" />
              <stop offset="1" stopColor="#4D248C" />
            </linearGradient>
            <radialGradient
              id={id("paint1_radial_352_10627")}
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(333 229) rotate(79.5939) scale(354.328 356.443)"
            >
              <stop stopColor="white" />
              <stop offset="0.55" stopColor="white" stopOpacity="0" />
              <stop offset="0.59045" stopColor="white" stopOpacity="0" />
              <stop offset="1" stopColor="white" stopOpacity="0" />
            </radialGradient>
            <linearGradient
              id={id("paint2_linear_352_10627")}
              x1="343.771"
              y1="15.8154"
              x2="343.771"
              y2="642.131"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#9E75EB" />
              <stop offset="0.55" stopColor="#7A45C7" />
              <stop offset="1" stopColor="#4D248C" />
            </linearGradient>
            <linearGradient
              id={id("paint3_linear_352_10627")}
              x1="88.6661"
              y1="213.412"
              x2="246.833"
              y2="532.859"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#6438FF" />
              <stop offset="0.3926" stopColor="#703BF8" />
              <stop offset="1" stopColor="#FF5DA4" />
            </linearGradient>
            <linearGradient
              id={id("paint4_linear_352_10627")}
              x1="88.6661"
              y1="213.412"
              x2="246.833"
              y2="532.859"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#6438FF" />
              <stop offset="0.3926" stopColor="#703BF8" />
              <stop offset="1" stopColor="#FF5DA4" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    );
  },
);

MayaSvgAvatar.displayName = "MayaSvgAvatar";
